import React, { useCallback, useState } from 'react';
import { Plane, Save, RefreshCw, Download, Clock, MapPin, Calendar } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import FlightParametersSection from './FlightParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import LayoverSection from './LayoverSection';

// Импорт данных о городах
import { russianCities, internationalCities, getCityTimezone, getDistanceBetweenCities } from './data/cities';

// Импорт утилитарных функций
import { isPositiveNumber } from '../../../../../utils/validation';
import { formatDate, formatDuration } from '../../../../../utils/formatting';
import { calculateDateDifference, convertTimezone } from '../../../../../utils/calculations/date';

/**
 * Компонент основной формы расчета времени полета
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  flightRoutes,
  setFlightRoutes,
  aircraftTypes,
  flightClasses,
  delayFactors,
  saveFlight 
}) => {
  
  // Состояние для имени сохраняемого маршрута
  const [saveFlightName, setSaveFlightName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeLayover' && !checked) {
        newState.layoverCity = 'Казань';
        newState.layoverDuration = '2';
      }
      
      if (name === 'includeDelays' && !checked) {
        newState.delayFactor = 'low';
      }

      // Если изменяется тип полета, корректируем связанные настройки
      if (name === 'flightType') {
        if (value === 'direct') {
          newState.includeLayover = false;
        } else if (value === 'connecting') {
          newState.includeLayover = true;
        }
      }

      // Если изменяется наличие обратного рейса
      if (name === 'isRoundTrip' && checked && !prev.returnDate) {
        // Устанавливаем дату возврата по умолчанию (через неделю после вылета)
        const departureDate = new Date(prev.departureDate);
        departureDate.setDate(departureDate.getDate() + 7);
        newState.returnDate = departureDate.toISOString().split('T')[0];
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      departureCity, arrivalCity, flightType, departureDate,
      isRoundTrip, returnDate, includeLayover, layoverCity, layoverDuration,
      aircraftType
    } = formData;

    // Проверка базовых полей
    if (!departureCity) newErrors.departureCity = 'Выберите город отправления.';
    if (!arrivalCity) newErrors.arrivalCity = 'Выберите город прибытия.';

    // Проверяем, что города отправления и прибытия разные
    if (departureCity === arrivalCity && departureCity) {
      newErrors.arrivalCity = 'Город прибытия должен отличаться от города отправления.';
    }

    // Проверка даты отправления
    if (!departureDate) newErrors.departureDate = 'Выберите дату отправления.';
    
    // Проверка даты возвращения, если это возвратный билет
    if (isRoundTrip) {
      if (!returnDate) {
        newErrors.returnDate = 'Выберите дату возвращения.';
      } else {
        const deptDate = new Date(departureDate);
        const retDate = new Date(returnDate);
        
        if (retDate < deptDate) {
          newErrors.returnDate = 'Дата возвращения должна быть позже даты отправления.';
        }
      }
    }

    // Проверки для пересадок
    if (includeLayover) {
      if (!layoverCity) newErrors.layoverCity = 'Выберите город пересадки.';
      
      // Проверка, что город пересадки отличается от городов отправления и прибытия
      if (layoverCity === departureCity || layoverCity === arrivalCity) {
        newErrors.layoverCity = 'Город пересадки должен отличаться от городов отправления и прибытия.';
      }

      const numLayoverDuration = parseFloatOrDefault(layoverDuration, -1);
      if (numLayoverDuration <= 0) newErrors.layoverDuration = 'Время пересадки должно быть положительным числом.';
      if (numLayoverDuration > 48) newErrors.layoverDuration = 'Время пересадки не может превышать 48 часов.';
    }

    // Проверка типа самолета
    if (!aircraftType) newErrors.aircraftType = 'Выберите тип самолета.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета времени полета
  const calculateFlightTime = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setFlightRoutes([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const {
          departureCity,
          arrivalCity,
          flightType,
          flightClass,
          departureDate,
          returnDate,
          isRoundTrip,
          includeLayover,
          layoverCity,
          layoverDuration,
          aircraftType,
          includeDelays,
          delayFactor,
          includeTimezones
        } = formData;

        // Находим тип самолета
        const aircraft = aircraftTypes.find(a => a.id === aircraftType);
        const avgSpeed = aircraft ? aircraft.avgSpeed : 850; // км/ч
        
        // Определяем фактор задержки
        const delayFactorObj = delayFactors.find(d => d.id === delayFactor);
        const delayMultiplier = includeDelays && delayFactorObj ? delayFactorObj.value : 0;

        // Получаем расстояния между городами
        let totalDistance = 0;
        let directDistance = getDistanceBetweenCities(departureCity, arrivalCity);
        
        if (includeLayover && layoverCity) {
          const distanceToCityA = getDistanceBetweenCities(departureCity, layoverCity);
          const distanceToCityB = getDistanceBetweenCities(layoverCity, arrivalCity);
          totalDistance = distanceToCityA + distanceToCityB;
        } else {
          totalDistance = directDistance;
        }

        // --- Рассчитываем время полета (в часах) ---
        const directFlightTime = directDistance / avgSpeed;
        const directFlightTimeWithDelays = directFlightTime * (1 + delayMultiplier);
        
        let totalFlightTime = 0;
        let flightTimeToLayover = 0;
        let flightTimeFromLayover = 0;
        
        if (includeLayover && layoverCity) {
          flightTimeToLayover = getDistanceBetweenCities(departureCity, layoverCity) / avgSpeed;
          flightTimeFromLayover = getDistanceBetweenCities(layoverCity, arrivalCity) / avgSpeed;
          
          // Добавляем задержки к каждому сегменту, если включены
          const flightTimeToLayoverWithDelays = flightTimeToLayover * (1 + delayMultiplier);
          const flightTimeFromLayoverWithDelays = flightTimeFromLayover * (1 + delayMultiplier);
          
          // Суммарное время с учетом пересадки
          totalFlightTime = flightTimeToLayoverWithDelays + parseFloat(layoverDuration) + flightTimeFromLayoverWithDelays;
        } else {
          totalFlightTime = directFlightTimeWithDelays;
        }

        // --- Рассчитываем даты и время ---
        const departureDateTime = new Date(departureDate);
        departureDateTime.setHours(10, 0, 0, 0); // Принимаем стандартное время вылета 10:00
        
        // Рассчитываем время прибытия с учетом полета
        const directArrivalDateTime = new Date(departureDateTime);
        const flightHours = Math.floor(directFlightTimeWithDelays);
        const flightMinutes = Math.round((directFlightTimeWithDelays - flightHours) * 60);
        directArrivalDateTime.setHours(
          directArrivalDateTime.getHours() + flightHours,
          directArrivalDateTime.getMinutes() + flightMinutes
        );

        // Если используем пересадки
        let layoverStartDateTime = null;
        let layoverEndDateTime = null;
        let finalArrivalDateTime = null;
        
        if (includeLayover && layoverCity) {
          // Время прибытия в город пересадки
          layoverStartDateTime = new Date(departureDateTime);
          const flightToLayoverHours = Math.floor(flightTimeToLayover * (1 + delayMultiplier));
          const flightToLayoverMinutes = Math.round((flightTimeToLayover * (1 + delayMultiplier) - flightToLayoverHours) * 60);
          layoverStartDateTime.setHours(
            layoverStartDateTime.getHours() + flightToLayoverHours,
            layoverStartDateTime.getMinutes() + flightToLayoverMinutes
          );
          
          // Время отправления из города пересадки
          layoverEndDateTime = new Date(layoverStartDateTime);
          const layoverHours = Math.floor(parseFloat(layoverDuration));
          const layoverMinutes = Math.round((parseFloat(layoverDuration) - layoverHours) * 60);
          layoverEndDateTime.setHours(
            layoverEndDateTime.getHours() + layoverHours,
            layoverEndDateTime.getMinutes() + layoverMinutes
          );
          
          // Время прибытия в конечный пункт назначения
          finalArrivalDateTime = new Date(layoverEndDateTime);
          const flightFromLayoverHours = Math.floor(flightTimeFromLayover * (1 + delayMultiplier));
          const flightFromLayoverMinutes = Math.round((flightTimeFromLayover * (1 + delayMultiplier) - flightFromLayoverHours) * 60);
          finalArrivalDateTime.setHours(
            finalArrivalDateTime.getHours() + flightFromLayoverHours,
            finalArrivalDateTime.getMinutes() + flightFromLayoverMinutes
          );
        } else {
          finalArrivalDateTime = directArrivalDateTime;
        }

        // --- Учитываем часовые пояса ---
        let departureTimezone = null;
        let arrivalTimezone = null;
        let layoverTimezone = null;
        
        if (includeTimezones) {
          departureTimezone = getCityTimezone(departureCity);
          arrivalTimezone = getCityTimezone(arrivalCity);
          
          if (includeLayover && layoverCity) {
            layoverTimezone = getCityTimezone(layoverCity);
            
            // Корректируем время с учетом часовых поясов
            if (departureTimezone && layoverTimezone) {
              const tzDifference = (layoverTimezone.offset - departureTimezone.offset);
              layoverStartDateTime.setHours(layoverStartDateTime.getHours() + tzDifference);
              layoverEndDateTime.setHours(layoverEndDateTime.getHours() + tzDifference);
            }
            
            // Корректируем время прибытия с учетом часовых поясов
            if (layoverTimezone && arrivalTimezone) {
              const tzDifference = (arrivalTimezone.offset - layoverTimezone.offset);
              finalArrivalDateTime.setHours(finalArrivalDateTime.getHours() + tzDifference);
            }
          } else {
            // Прямой рейс - корректируем время прибытия
            if (departureTimezone && arrivalTimezone) {
              const tzDifference = (arrivalTimezone.offset - departureTimezone.offset);
              finalArrivalDateTime.setHours(finalArrivalDateTime.getHours() + tzDifference);
            }
          }
        }

        // --- Обратный рейс (если включен) ---
        let returnDepartureDateTime = null;
        let returnArrivalDateTime = null;
        let returnLayoverStartDateTime = null;
        let returnLayoverEndDateTime = null;
        
        if (isRoundTrip && returnDate) {
          returnDepartureDateTime = new Date(returnDate);
          returnDepartureDateTime.setHours(16, 0, 0, 0); // Принимаем стандартное время вылета обратно - 16:00
          
          if (includeLayover && layoverCity) {
            // Процесс аналогичен прямому рейсу, но в обратном направлении
            returnLayoverStartDateTime = new Date(returnDepartureDateTime);
            const returnFlightToLayoverHours = Math.floor(flightTimeFromLayover * (1 + delayMultiplier));
            const returnFlightToLayoverMinutes = Math.round((flightTimeFromLayover * (1 + delayMultiplier) - returnFlightToLayoverHours) * 60);
            returnLayoverStartDateTime.setHours(
              returnLayoverStartDateTime.getHours() + returnFlightToLayoverHours,
              returnLayoverStartDateTime.getMinutes() + returnFlightToLayoverMinutes
            );
            
            returnLayoverEndDateTime = new Date(returnLayoverStartDateTime);
            const returnLayoverHours = Math.floor(parseFloat(layoverDuration));
            const returnLayoverMinutes = Math.round((parseFloat(layoverDuration) - returnLayoverHours) * 60);
            returnLayoverEndDateTime.setHours(
              returnLayoverEndDateTime.getHours() + returnLayoverHours,
              returnLayoverEndDateTime.getMinutes() + returnLayoverMinutes
            );
            
            returnArrivalDateTime = new Date(returnLayoverEndDateTime);
            const returnFlightFromLayoverHours = Math.floor(flightTimeToLayover * (1 + delayMultiplier));
            const returnFlightFromLayoverMinutes = Math.round((flightTimeToLayover * (1 + delayMultiplier) - returnFlightFromLayoverHours) * 60);
            returnArrivalDateTime.setHours(
              returnArrivalDateTime.getHours() + returnFlightFromLayoverHours,
              returnArrivalDateTime.getMinutes() + returnFlightFromLayoverMinutes
            );
            
            // Учитываем часовые пояса для обратного рейса
            if (includeTimezones) {
              if (arrivalTimezone && layoverTimezone) {
                const tzDifference = (layoverTimezone.offset - arrivalTimezone.offset);
                returnLayoverStartDateTime.setHours(returnLayoverStartDateTime.getHours() + tzDifference);
                returnLayoverEndDateTime.setHours(returnLayoverEndDateTime.getHours() + tzDifference);
              }
              
              if (layoverTimezone && departureTimezone) {
                const tzDifference = (departureTimezone.offset - layoverTimezone.offset);
                returnArrivalDateTime.setHours(returnArrivalDateTime.getHours() + tzDifference);
              }
            }
          } else {
            // Прямой обратный рейс
            returnArrivalDateTime = new Date(returnDepartureDateTime);
            returnArrivalDateTime.setHours(
              returnArrivalDateTime.getHours() + flightHours,
              returnArrivalDateTime.getMinutes() + flightMinutes
            );
            
            // Учитываем часовые пояса
            if (includeTimezones && arrivalTimezone && departureTimezone) {
              const tzDifference = (departureTimezone.offset - arrivalTimezone.offset);
              returnArrivalDateTime.setHours(returnArrivalDateTime.getHours() + tzDifference);
            }
          }
        }

        // --- Подготовка маршрутов для визуализации ---
        const routes = [];
        
        // Прямой маршрут
        if (includeLayover && layoverCity) {
          // Первый сегмент полета (до города пересадки)
          routes.push({
            segment: 1,
            direction: 'outbound',
            departureCity,
            arrivalCity: layoverCity,
            departureTime: departureDateTime,
            arrivalTime: layoverStartDateTime,
            flightDuration: getDistanceBetweenCities(departureCity, layoverCity) / avgSpeed * (1 + delayMultiplier),
            distance: getDistanceBetweenCities(departureCity, layoverCity),
            aircraft: aircraft?.examples || 'Обычный самолет',
            isLayover: false
          });
          
          // Пересадка
          routes.push({
            segment: 2,
            direction: 'outbound',
            departureCity: layoverCity,
            arrivalCity: layoverCity,
            departureTime: layoverStartDateTime,
            arrivalTime: layoverEndDateTime,
            flightDuration: 0,
            distance: 0,
            aircraft: '',
            isLayover: true,
            layoverDuration: parseFloat(layoverDuration)
          });
          
          // Второй сегмент полета (от города пересадки до конечного пункта)
          routes.push({
            segment: 3,
            direction: 'outbound',
            departureCity: layoverCity,
            arrivalCity,
            departureTime: layoverEndDateTime,
            arrivalTime: finalArrivalDateTime,
            flightDuration: getDistanceBetweenCities(layoverCity, arrivalCity) / avgSpeed * (1 + delayMultiplier),
            distance: getDistanceBetweenCities(layoverCity, arrivalCity),
            aircraft: aircraft?.examples || 'Обычный самолет',
            isLayover: false
          });
        } else {
          // Прямой рейс без пересадок
          routes.push({
            segment: 1,
            direction: 'outbound',
            departureCity,
            arrivalCity,
            departureTime: departureDateTime,
            arrivalTime: finalArrivalDateTime,
            flightDuration: directFlightTimeWithDelays,
            distance: directDistance,
            aircraft: aircraft?.examples || 'Обычный самолет',
            isLayover: false
          });
        }
        
        // Добавляем обратный маршрут, если это возвратный билет
        if (isRoundTrip && returnDepartureDateTime && returnArrivalDateTime) {
          if (includeLayover && layoverCity) {
            // Первый сегмент обратного полета
            routes.push({
              segment: 4,
              direction: 'return',
              departureCity: arrivalCity,
              arrivalCity: layoverCity,
              departureTime: returnDepartureDateTime,
              arrivalTime: returnLayoverStartDateTime,
              flightDuration: getDistanceBetweenCities(arrivalCity, layoverCity) / avgSpeed * (1 + delayMultiplier),
              distance: getDistanceBetweenCities(arrivalCity, layoverCity),
              aircraft: aircraft?.examples || 'Обычный самолет',
              isLayover: false
            });
            
            // Обратная пересадка
            routes.push({
              segment: 5,
              direction: 'return',
              departureCity: layoverCity,
              arrivalCity: layoverCity,
              departureTime: returnLayoverStartDateTime,
              arrivalTime: returnLayoverEndDateTime,
              flightDuration: 0,
              distance: 0,
              aircraft: '',
              isLayover: true,
              layoverDuration: parseFloat(layoverDuration)
            });
            
            // Второй сегмент обратного полета
            routes.push({
              segment: 6,
              direction: 'return',
              departureCity: layoverCity,
              arrivalCity: departureCity,
              departureTime: returnLayoverEndDateTime,
              arrivalTime: returnArrivalDateTime,
              flightDuration: getDistanceBetweenCities(layoverCity, departureCity) / avgSpeed * (1 + delayMultiplier),
              distance: getDistanceBetweenCities(layoverCity, departureCity),
              aircraft: aircraft?.examples || 'Обычный самолет',
              isLayover: false
            });
          } else {
            // Прямой обратный рейс
            routes.push({
              segment: 4,
              direction: 'return',
              departureCity: arrivalCity,
              arrivalCity: departureCity,
              departureTime: returnDepartureDateTime,
              arrivalTime: returnArrivalDateTime,
              flightDuration: directFlightTimeWithDelays,
              distance: directDistance,
              aircraft: aircraft?.examples || 'Обычный самолет',
              isLayover: false
            });
          }
        }

        // --- Подготовка итоговых результатов ---
        const totalFlightDistance = routes.reduce((sum, route) => 
          route.isLayover ? sum : sum + route.distance, 0);
        
        let totalFlightDurationOutbound = routes
          .filter(route => route.direction === 'outbound' && !route.isLayover)
          .reduce((sum, route) => sum + route.flightDuration, 0);
        
        let totalLayoverDurationOutbound = routes
          .filter(route => route.direction === 'outbound' && route.isLayover)
          .reduce((sum, route) => sum + route.layoverDuration, 0);
        
        let totalJourneyDurationOutbound = totalFlightDurationOutbound + totalLayoverDurationOutbound;
        
        let totalFlightDurationReturn = 0;
        let totalLayoverDurationReturn = 0;
        let totalJourneyDurationReturn = 0;
        
        if (isRoundTrip) {
          totalFlightDurationReturn = routes
            .filter(route => route.direction === 'return' && !route.isLayover)
            .reduce((sum, route) => sum + route.flightDuration, 0);
          
          totalLayoverDurationReturn = routes
            .filter(route => route.direction === 'return' && route.isLayover)
            .reduce((sum, route) => sum + route.layoverDuration, 0);
          
          totalJourneyDurationReturn = totalFlightDurationReturn + totalLayoverDurationReturn;
        }
        
        const totalJourneyDuration = totalJourneyDurationOutbound + totalJourneyDurationReturn;
        
        // Разница между датами вылета и прилета (только для исходящего рейса)
        const calculatedDifference = calculateDateDifference(
          departureDateTime,
          finalArrivalDateTime
        );

        const finalResults = {
          // Основные параметры маршрута
          departureCity,
          arrivalCity,
          layoverCity: includeLayover ? layoverCity : null,
          
          // Расстояния и времена полета
          directDistance,
          totalDistance: totalFlightDistance,
          directFlightTime: directFlightTime,
          totalFlightTimeOutbound: totalFlightDurationOutbound,
          totalLayoverTimeOutbound: totalLayoverDurationOutbound,
          totalJourneyTimeOutbound: totalJourneyDurationOutbound,
          totalFlightTimeReturn: totalFlightDurationReturn,
          totalLayoverTimeReturn: totalLayoverDurationReturn,
          totalJourneyTimeReturn: totalJourneyDurationReturn,
          totalJourneyTime: totalJourneyDuration,
          
          // Даты и времена
          departureDate: departureDateTime,
          arrivalDate: finalArrivalDateTime,
          returnDepartureDate: returnDepartureDateTime,
          returnArrivalDate: returnArrivalDateTime,
          
          // Параметры расчета
          aircraft: aircraft?.id || 'medium',
          aircraftName: aircraft?.name || 'Средний самолет',
          aircraftSpeed: avgSpeed,
          flightClass,
          includeDelays,
          delayFactor,
          delayMultiplier,
          
          // Информация о часовых поясах
          includeTimezones,
          departureTimezone,
          arrivalTimezone,
          layoverTimezone,
          
          // Дополнительная информация
          isRoundTrip,
          dateDifference: calculatedDifference
        };

        setResults(finalResults);
        setFlightRoutes(routes);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setFlightRoutes([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setFlightRoutes, setIsCalculating, aircraftTypes, delayFactors]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      departureCity: 'Москва',
      arrivalCity: 'Санкт-Петербург',
      flightType: 'direct',
      flightClass: 'economy',
      departureDate: new Date().toISOString().split('T')[0],
      returnDate: '',
      isRoundTrip: false,
      includeLayover: false,
      layoverCity: 'Казань',
      layoverDuration: '2',
      aircraftType: 'medium',
      includeDelays: false,
      delayFactor: 'low',
      includeTimezones: true,
    });
    setResults(null);
    setFlightRoutes([]);
    setErrors({});
    setSaveFlightName('');
    setShowSaveDialog(false);
  };

  // Экспорт маршрута в CSV
  const exportToCSV = () => {
    if (!flightRoutes || flightRoutes.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Сегмент', 'Направление', 'Город отправления', 'Город прибытия', 'Время отправления', 'Время прибытия', 'Длительность', 'Расстояние (км)', 'Тип самолета'];

    const rows = flightRoutes.map(route => {
      const direction = route.direction === 'outbound' ? 'Туда' : 'Обратно';
      const duration = route.isLayover 
        ? `Пересадка ${route.layoverDuration} ч.` 
        : `${Math.floor(route.flightDuration)}ч ${Math.round((route.flightDuration % 1) * 60)}м`;
      
      return [
        route.segment,
        direction,
        route.departureCity,
        route.arrivalCity,
        route.departureTime.toLocaleString('ru-RU'),
        route.arrivalTime.toLocaleString('ru-RU'),
        duration,
        route.isLayover ? '-' : Math.round(route.distance),
        route.isLayover ? '-' : route.aircraft
      ].join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      '', 
      'ИТОГО:', 
      '', 
      '',
      '', 
      '',
      `${Math.floor(results.totalJourneyTime)}ч ${Math.round((results.totalJourneyTime % 1) * 60)}м`,
      Math.round(results.totalDistance),
      ''
    ].join(';');
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Маршрут:', `${results.departureCity} - ${results.arrivalCity}${results.layoverCity ? ' через ' + results.layoverCity : ''}`],
      ['Тип самолета:', results.aircraftName],
      ['Класс обслуживания:', formData.flightClass === 'economy' ? 'Эконом' : formData.flightClass === 'comfort' ? 'Комфорт' : formData.flightClass === 'business' ? 'Бизнес' : 'Первый'],
      ['Дата вылета:', new Date(formData.departureDate).toLocaleDateString('ru-RU')],
      ['Дата возвращения:', formData.isRoundTrip ? new Date(formData.returnDate).toLocaleDateString('ru-RU') : '-'],
      ['Общее расстояние:', `${Math.round(results.totalDistance)} км`],
      ['Общее время в пути:', `${Math.floor(results.totalJourneyTime)}ч ${Math.round((results.totalJourneyTime % 1) * 60)}м`],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'маршрут_полета.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения маршрута
  const handleSaveFlight = () => {
    if (showSaveDialog) {
      const savedResult = saveFlight(saveFlightName || null);
      if (savedResult) {
        alert(`Маршрут "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveFlightName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateFlightTime();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Plane className="w-6 h-6 text-blue-500" />
          Параметры маршрута
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры полета */}
        <FlightParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          aircraftTypes={aircraftTypes}
          flightClasses={flightClasses}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          delayFactors={delayFactors}
        />

        {/* Параметры пересадки */}
        {formData.includeLayover && (
          <LayoverSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />
        )}

        {/* Диалог сохранения маршрута */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveFlightName}
                onChange={(e) => setSaveFlightName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveFlight}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Plane className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать маршрут'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {flightRoutes.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать маршрут (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveFlight}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить маршрут
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;